import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Button,
  keyframes
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { AI_API_URL } from '../../constants';

const ProgressModal = ({ 
  open, 
  currentStep, 
  percentage, 
  currentMessage, 
  estimatedRemainingTime,
  videoDuration = 0,
  onCancel,
  progressData = {},
  visualProgress = null, // 시각적 진행률 (부드러운 애니메이션용)
  userId = null, // Firebase 실시간 데이터용
  historyId = null // Firebase 실시간 데이터용
}) => {
  const { t } = useTranslation();
  
  // ✅ 모달 상태 관리 (progress | confirmCancel | cancelling)
  const [modalState, setModalState] = useState('progress');
  
  // ✅ Firebase에서 실시간 데이터 읽기
  const [firebaseData, setFirebaseData] = useState(null);
  
  // ✅ 시각적 진행률 (부드러운 애니메이션용) - 기존 useProgress.js 로직 복사
  const [internalVisualProgress, setInternalVisualProgress] = useState(0);
  const [targetProgress, setTargetProgress] = useState(0);
  const progressIntervalRef = useRef(null);
  
  useEffect(() => {
    if (!userId || !historyId || !open) {
      return;
    }
    
    const docRef = doc(db, "users", userId, "history", historyId);
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setFirebaseData({
          processingProgress: data.processingProgress,
          status: data.status,
          backgroundProcessing: data.backgroundProcessing
        });
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [userId, historyId, open]);
  
  // 분석 완료 여부 확인
  const isAnalysisComplete = firebaseData?.status === 'active';
  
  // ✅ 시각적 진행률 업데이트 useEffect (기존 useProgress.js 로직 정확히 복사)
  useEffect(() => {
    if (!open) {
      // 모달이 닫혀있으면 모든 진행률 리셋
      setInternalVisualProgress(0);
      setTargetProgress(0);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      return;
    }

    // ✅ 타겟 진행률 설정 (기존 로직과 동일하게 메인 useEffect 내부에서 설정)
    const currentProgress = firebaseData?.processingProgress !== undefined 
      ? firebaseData.processingProgress 
      : (percentage || 0);
    setTargetProgress(currentProgress);

    // 기존 인터벌 정리
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    // 시각적 진행률 업데이트 인터벌
    progressIntervalRef.current = setInterval(() => {
      setInternalVisualProgress(current => {
        const target = targetProgress;
        
        // ✅ 목표값이 100%면 즉시 100%로 설정
        if (target === 100) {
          return 100;
        }
        
        // 목표값에 도달했으면 더 이상 증가하지 않음
        if (current >= target) {
          // 하지만 목표값이 100%가 아니라면 조금씩 증가
          if (target < 100) {
            // 80% 이상에서는 더 적극적인 시각적 증가 (최대 +15%까지)
            if (target >= 80) {
              const maxProgress = Math.min(target + 15, 99);
              if (current < maxProgress) {
                return current + 0.1; // 천천히 하지만 더 많이 증가
              }
            } else {
              // 80% 미만에서는 기존 로직 유지 (최대 +5%까지)
              const maxProgress = Math.min(target + 5, 100);
              if (current < maxProgress) {
                return current + 0.2; // 매우 천천히 증가
              }
            }
          }
          return current;
        }
        
        // 목표값으로 빠르게 수렴
        const diff = target - current;
        if (diff > 10) {
          return current + 2; // 큰 차이가 나면 빠르게
        } else if (diff > 1) {
          return current + 0.5; // 작은 차이가 나면 천천히
        } else {
          return target; // 거의 근접하면 목표값으로
        }
      });
    }, 100); // 100ms마다 업데이트

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [open, targetProgress]);

  // ✅ 실제 진행률이 업데이트되면 타겟 진행률 변경 (기존 로직 복사)
  useEffect(() => {
    if (open && firebaseData?.processingProgress !== undefined) {
      setTargetProgress(firebaseData.processingProgress);
    }
  }, [firebaseData?.processingProgress, open]);

  // ✅ 모달이 열릴 때 초기 진행률 설정 (기존 openProgressModal 로직 복사)
  useEffect(() => {
    if (open) {
      // 🚨 모달이 열릴 때마다 상태 초기화
      setModalState('progress');
      
      const initialProgress = firebaseData?.processingProgress !== undefined 
        ? firebaseData.processingProgress 
        : (percentage || 0);
      
      // 백그라운드 재접속 시 초기 진행률도 시각적으로 반영 (기존 로직)
      if (initialProgress > 0) {
        setInternalVisualProgress(initialProgress);
        setTargetProgress(initialProgress);
      }
    } else {
      // 🚨 모달이 닫힐 때도 상태 초기화
      setModalState('progress');
    }
     }, [open]); // open 상태 변경 시에만 실행

   // 애니메이션 키프레임 정의
  const pulseAnimation = keyframes`
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  `;

  const glowAnimation = keyframes`
    0% {
      box-shadow: 0 0 5px rgba(25, 118, 210, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(25, 118, 210, 0.6), 0 0 30px rgba(25, 118, 210, 0.4);
    }
    100% {
      box-shadow: 0 0 5px rgba(25, 118, 210, 0.3);
    }
  `;

  const shimmerAnimation = keyframes`
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  `;

  const bounceAnimation = keyframes`
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-3px);
    }
    60% {
      transform: translateY(-2px);
    }
  `;

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return t('time.seconds', { seconds });
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds === 0 
        ? t('time.minutes', { minutes }) 
        : t('time.minutesSeconds', { minutes, seconds: remainingSeconds });
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return t('time.hoursMinutes', { hours, minutes });
    }
  };

  // 현재 백엔드에서 전달하는 데이터 사용
  // ✅ 시각적 진행률(애니메이션 효과)을 사용 - 기존 useProgress.js 로직과 동일
  const currentProgress = internalVisualProgress;
  
  // ✅ 진행률 숫자를 기반으로 메시지 계산 (useProgress의 getMessageByPercentage 로직 복제)
  const getProgressMessage = (percentage) => {
    if (percentage >= 0 && percentage <= 10) {
      return t('analyze.extractingSubtitles'); // "영상의 자막을 추출 중입니다."
    } else if (percentage > 10 && percentage < 30) {
      return t('analyze.translatingSubtitles'); // "추출된 자막을 번역 중입니다."
    } else if (percentage >= 30 && percentage < 60) {
      return t('analyze.summarizingContent'); // "영상의 자막을 요약 중입니다."
    } else if (percentage >= 60 && percentage < 70) {
      return t('analyze.organizingKey'); // "핵심 내용을 정리 중입니다."
    } else if (percentage >= 70 && percentage < 80) {
      return t('analyze.organizingChapters'); // "챕터별로 핵심 내용을 정리 중입니다."
    } else if (percentage >= 80 && percentage <= 100) {
      return t('analyze.generatingQuestions'); // "심층 이해를 위한 추천질문을 생성 중입니다."
    }
    return t('common.startingAnalysis');
  };
  
  // 분석 완료 시 완료 메시지 표시, 그렇지 않으면 진행률 기반 메시지
  const currentMsg = isAnalysisComplete 
    ? t('analyze.analysisCompleted') 
    : (currentMessage || getProgressMessage(currentProgress));

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        {/* 진행률 원형 표시 */}
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
          {/* 배경 원 */}
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              left: -10,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(66, 165, 245, 0.1))',
              animation: `${glowAnimation} 2s ease-in-out infinite`,
            }}
          />
          
          <CircularProgress
            variant="determinate"
            value={currentProgress}
            size={80}
            thickness={4}
            sx={{
              color: '#1976d2',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
                filter: 'drop-shadow(0 0 6px rgba(25, 118, 210, 0.4))',
              }
            }}
          />
          
          {/* 중앙 텍스트 */}
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            <Typography 
              variant="h6" 
              component="div" 
              color="text.secondary"
              sx={{
                fontWeight: 'bold',
                animation: currentProgress < 100 ? `${bounceAnimation} 1s ease-in-out infinite` : 'none'
              }}
            >
              {Math.round(currentProgress)}%
            </Typography>
          </Box>
        </Box>

        {/* 현재 단계 표시 */}
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 1, 
            fontWeight: 600,
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: `${shimmerAnimation} 3s ease-in-out infinite`,
            backgroundSize: '200% 100%'
          }}
        >
          {currentMsg}
        </Typography>

        {/* 영상 정보 표시 */}
        {videoDuration > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('analyze.videoDuration')}: {formatTime(videoDuration)}
          </Typography>
        )}

        {/* 예상 남은 시간 */}
        {estimatedRemainingTime > 0 && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 3,
              animation: `${pulseAnimation} 1.5s ease-in-out infinite`
            }}
          >
            {t('analyze.estimatedTime')}: {formatTime(estimatedRemainingTime)}
          </Typography>
        )}

        {/* 진행률 바 */}
        <LinearProgress 
          variant="determinate" 
          value={currentProgress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: '#f0f0f0',
            overflow: 'hidden',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
              backgroundSize: '200% 100%',
              animation: `${shimmerAnimation} 2s linear infinite`,
              filter: 'drop-shadow(0 0 4px rgba(25, 118, 210, 0.3))'
            }
          }} 
        />

        {/* 단계별 진행 상황 (새로운 구간 기준) */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {[
            { step: 'transcript', label: t('analyze.stepTranscript'), threshold: 30, icon: '📄' },
            { step: 'summary', label: t('analyze.stepSummary'), threshold: 60, icon: '📋' },
            { step: 'final_summary', label: t('analyze.stepFinalSummary'), threshold: 70, icon: '🎯' },
            { step: 'chapters', label: t('analyze.stepChapters'), threshold: 80, icon: '📑' },
            { step: 'completion', label: t('analyze.stepCompletion'), threshold: 100, icon: '✅' }
          ].map((stepData, index) => {
            const isCompleted = currentProgress >= stepData.threshold;
            const isCurrent = currentProgress >= (index > 0 ? [30, 60, 70, 80][index-1] : 0) && currentProgress < stepData.threshold;
            
            return (
              <Box key={stepData.step} sx={{ textAlign: 'center', flex: 1 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? '#1976d2' : isCurrent ? '#42a5f5' : '#e0e0e0',
                    color: isCompleted || isCurrent ? 'white' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    margin: '0 auto 4px auto',
                    transition: 'all 0.3s ease',
                    animation: isCurrent ? `${pulseAnimation} 1s ease-in-out infinite` : 'none',
                    boxShadow: isCompleted ? '0 0 8px rgba(25, 118, 210, 0.4)' : 'none'
                  }}
                >
                  <span style={{ fontSize: '8px' }}>{stepData.icon}</span>
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: isCompleted ? '#1976d2' : isCurrent ? '#42a5f5' : '#666',
                    fontWeight: isCurrent ? 600 : 400,
                    fontSize: '8px',
                    animation: isCurrent ? `${bounceAnimation} 2s ease-in-out infinite` : 'none'
                  }}
                >
                  {stepData.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* 분석 취소/확인 버튼 */}
        {onCancel && (
          <Box sx={{ mt: 3 }}>
            {/* 기본 진행률 표시 상태 */}
            {modalState === 'progress' && (
              <Button
                variant={isAnalysisComplete ? "contained" : "outlined"}
                color={isAnalysisComplete ? "primary" : "error"}
                onClick={() => {
                  if (isAnalysisComplete) {
                    // 분석 완료 시 확인 버튼
                    onCancel();
                  } else {
                    // 분석 중일 때 취소 확인 단계로 이동
                    setModalState('confirmCancel');
                  }
                }}
              >
                {isAnalysisComplete ? t('common.confirm') : t('analyze.cancelAnalysis')}
              </Button>
            )}

            {/* 취소 확인 상태 */}
            {modalState === 'confirmCancel' && (
              <Box>
                {/* 분석이 완료되었으면 취소 불가능 - 확인 버튼만 표시 */}
                {isAnalysisComplete ? (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                      {t('analyze.analysisCompleted')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onCancel}
                    >
                      {t('common.confirm')}
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
                      {t('analyze.confirmCancelTitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setModalState('progress')}
                      >
                        {t('analyze.continueAnalysis')}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={async () => {
                          setModalState('cancelling');
                          
                          try {
                            console.log('🔴 [분석 취소] 백그라운드 분석 중단 시작');
                            
                            // 1. 백그라운드 분석 중단 API
                            const cancelResponse = await fetch(`${AI_API_URL}/api/streaming/background-tasks/${userId}/${historyId}`, {
                              method: 'DELETE',
                              headers: { 'user-id': userId }
                            });
                            
                            if (cancelResponse.ok) {
                              console.log('✅ [분석 취소] 백그라운드 분석 중단 성공');
                            } else {
                              console.warn('⚠️ [분석 취소] 백그라운드 분석 중단 실패 (계속 진행)');
                            }
                            
                            // 2. 히스토리 삭제 API
                            const deleteResponse = await fetch(`${AI_API_URL}/api/delete-history/${historyId}`, {
                              method: 'DELETE',
                              headers: { 'user-id': userId }
                            });
                            
                            if (deleteResponse.ok) {
                              console.log('✅ [분석 취소] 히스토리 삭제 성공');
                            } else {
                              console.warn('⚠️ [분석 취소] 히스토리 삭제 실패');
                            }
                            
                            // 3. 모달 닫기 & 홈으로 이동
                            onCancel();
                            setTimeout(() => {
                              window.location.href = '/';
                            }, 100);
                            
                          } catch (error) {
                            console.error('❌ [분석 취소] 에러:', error);
                            // 에러 발생 시에도 모달 닫기
                            onCancel();
                          }
                        }}
                      >
                        {t('analyze.cancelAnalysis')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* 취소 중 상태 */}
            {modalState === 'cancelling' && (
              <Box>
                <CircularProgress size={24} sx={{ mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('analyze.cancelling')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProgressModal; 